"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

// Interactive fake shell behind the footer easter egg (THE BACKEND?). It boots
// with a "you found it" greeting, then drops you into a deadpan terminal that
// behaves like a real (locked-down) Linux box: a small virtual filesystem you
// can cd/ls/cat around, bash-accurate error messages, ps/df/uname noise, and
// tab completion. Nothing leaves the browser — intrusive commands fail the way
// they would on a box you don't own. Themed to the per-load --primary so it
// matches the matrix glow.

const BOOT = [
  "$ access --the-backend",
  "> granted ✦ you found it.",
  "> not many scroll this far. nice.",
  "> — josh",
  "",
  "shell ready. type 'help' if you're curious.",
].join("\n");

// Hidden until `ls -a`. Reading it reveals a code to paste back in, which
// unlocks an unlisted page. The filename looks like noise on purpose.
const HIDDEN_FILE = ".k9x7_f3a2";
const UNLOCK_CODE = "unlock-3f9a2c";
const SECRET_PATH = "/backdoor";
// `sudo ascend` is the one sudo that works: it hands off to the ASCEND
// universe console, a separate app served (unlisted) at /ascend/. Deliberately
// left out of `help` and tab completion.
const ASCEND_PATH = "/ascend/";
const HOME = "/home/guest";
const HOST = "backend";

// ---------------------------------------------------------------------------
// virtual filesystem
// ---------------------------------------------------------------------------

type FsNode = {
  dir?: boolean;
  content?: string[];
  mode: string;
  owner: string;
  group: string;
  date: string;
  size?: number; // override for files whose content isn't their real "size"
  denied?: boolean; // unreadable file / un-openable dir
};

const d = (opts: Partial<FsNode> = {}): FsNode => ({
  dir: true,
  mode: "drwxr-xr-x",
  owner: "root",
  group: "root",
  date: "Feb  9  2024",
  ...opts,
});
const f = (content: string[], opts: Partial<FsNode> = {}): FsNode => ({
  content,
  mode: "-rw-r--r--",
  owner: "root",
  group: "root",
  date: "Feb  9  2024",
  ...opts,
});

// What `cat` on an ELF binary actually looks like: mostly unprintable garbage
// with the loader path peeking through.
const ELF_JUNK = [
  "\u007fELF\u0002\u0001\u0001\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0000\u0003\u0000>\u0000\u0001\u0000\u0000\u0000@\u0018\u0000\u0000\u0000\u0000\u0000\u0000@\u00008\u0000\u000d\u0000@\u0000\u001f\u0000\u001e\u0000\u0006/lib64/ld-linux-x86-64.so.2\u0000GNU\u0000\u0002\u0000\u0000\u0000\u0006\u0000\u0000\u0000 \u0000\u0000\u0000\u0004\u0000\u0000\u0000",
];

const FS: Record<string, FsNode> = {
  "/": d(),
  "/bin": d(),
  "/etc": d(),
  "/home": d(),
  "/proc": d({ mode: "dr-xr-xr-x", date: "Jun 12 09:41" }),
  "/root": d({ mode: "drwx------", denied: true }),
  "/tmp": d({ mode: "drwxrwxrwt", date: "Jun 12 09:41" }),
  "/usr": d(),
  "/usr/bin": d(),
  "/var": d(),
  "/var/log": d(),
  "/var/www": d({ owner: "www-data", group: "www-data" }),
  "/var/www/html": d({ owner: "www-data", group: "www-data" }),

  "/etc/passwd": f([
    "root:x:0:0:root:/root:/bin/bash",
    "daemon:x:1:1:daemon:/usr/sbin:/usr/sbin/nologin",
    "bin:x:2:2:bin:/bin:/usr/sbin/nologin",
    "www-data:x:33:33:www-data:/var/www:/usr/sbin/nologin",
    "sshd:x:105:65534::/run/sshd:/usr/sbin/nologin",
    "josh:x:1000:1000:Josh Wood:/home/josh:/bin/bash",
    "guest:x:1001:1001:guest:/home/guest:/bin/rbash",
  ]),
  "/etc/shadow": f([], {
    mode: "-rw-r-----",
    group: "shadow",
    denied: true,
    size: 892,
  }),
  "/etc/hostname": f([HOST]),
  "/etc/hosts": f([
    "127.0.0.1\tlocalhost",
    "127.0.1.1\tbackend",
    "",
    "::1     ip6-localhost ip6-loopback",
  ]),
  "/etc/motd": f([
    "Unauthorized access to this system is prohibited.",
    "All activity is monitored and reported.",
  ]),

  "/proc/version": f([
    "Linux version 6.1.0-18-amd64 (debian-kernel@lists.debian.org) (gcc-12 (Debian 12.2.0-14) 12.2.0) #1 SMP PREEMPT_DYNAMIC Debian 6.1.76-1 (2024-02-01)",
  ], { mode: "-r--r--r--", date: "Jun 12 09:41" }),
  "/proc/cpuinfo": f([
    "processor\t: 0",
    "vendor_id\t: GenuineIntel",
    "model name\t: Intel(R) Xeon(R) CPU E5-2680 v4 @ 2.40GHz",
    "cpu MHz\t\t: 2399.996",
    "cache size\t: 35840 KB",
  ], { mode: "-r--r--r--", date: "Jun 12 09:41" }),
  "/proc/meminfo": f([
    "MemTotal:        4021840 kB",
    "MemFree:         3037884 kB",
    "MemAvailable:    3341368 kB",
  ], { mode: "-r--r--r--", date: "Jun 12 09:41" }),
  "/proc/uptime": f(["3675421.18 3612200.55"], {
    mode: "-r--r--r--",
    date: "Jun 12 09:41",
  }),

  "/var/log/auth.log": f([], {
    mode: "-rw-r-----",
    owner: "syslog",
    group: "adm",
    denied: true,
    size: 48213,
    date: "Jun 12 09:41",
  }),
  "/var/log/nginx": d({
    mode: "drwxr-x---",
    owner: "www-data",
    group: "adm",
    denied: true,
  }),
  "/var/www/html/index.html": f(
    [
      "<!doctype html>",
      "<html>",
      "  <head><title>it works</title></head>",
      "  <body>",
      "    <!-- you're poking around the inside. the real site is upstairs. -->",
      "  </body>",
      "</html>",
    ],
    { owner: "www-data", group: "www-data" }
  ),

  "/home/guest": d({ owner: "guest", group: "guest", date: "Jun 12 09:41" }),
  "/home/josh": d({ mode: "drwx------", owner: "josh", group: "josh", denied: true }),

  "/home/guest/readme.txt": f(
    [
      "internal backend — authorized personnel only.",
      "you went looking, though. respect. 🫡",
    ],
    { owner: "guest", group: "guest", date: "Jun 12 09:41" }
  ),
  "/home/guest/secrets.env": f(
    ["API_KEY=nice_try_404", "DB_PASSWORD=hunter2", "ADMIN_TOKEN=you_wish"],
    { mode: "-rw-------", owner: "guest", group: "guest", date: "Jun 12 09:41" }
  ),
  "/home/guest/flag.txt": f(
    [
      "flag{you_actually_read_this_far}",
      "tell josh you found it. that's the whole prize.",
    ],
    { owner: "guest", group: "guest", date: "Jun 12 09:41" }
  ),
  "/home/guest/todo.md": f(
    ["- [x] bury this under the footer", "- [ ] touch grass"],
    { owner: "guest", group: "guest", date: "Jun 12 09:41" }
  ),
  "/home/guest/.bash_history": f(
    ["ls -la", "cat readme.txt", "sudo su", "cat /etc/shadow", "history -c"],
    { mode: "-rw-------", owner: "guest", group: "guest", date: "Jun 12 09:41" }
  ),
  [`/home/guest/${HIDDEN_FILE}`]: f(
    [
      "psst — you weren't meant to find this one.",
      "paste this into the shell and hit enter:",
      "",
      `  ${UNLOCK_CODE}`,
    ],
    { owner: "guest", group: "guest", date: "Jun 12 09:41" }
  ),
};

// A believable /bin and /usr/bin so poking around doesn't hit empty dirs.
const binSize = (name: string) =>
  35000 + ((name.charCodeAt(0) * 2971 + name.length * 12007) % 90000);
for (const b of ["bash", "cat", "date", "df", "echo", "grep", "hostname", "ls", "mkdir", "ping", "ps", "rbash", "rm", "sh", "su", "touch"]) {
  FS["/bin/" + b] = f(ELF_JUNK, {
    mode: "-rwxr-xr-x",
    size: binSize(b),
  });
}
for (const b of ["curl", "env", "free", "head", "id", "ssh", "sudo", "tail", "uname", "uptime", "wget", "which", "whoami"]) {
  FS["/usr/bin/" + b] = f(ELF_JUNK, {
    mode: b === "sudo" ? "-rwsr-xr-x" : "-rwxr-xr-x",
    size: binSize(b),
  });
}

const ENV: Record<string, string> = {
  SHELL: "/bin/rbash",
  HOSTNAME: HOST,
  LOGNAME: "guest",
  HOME: HOME,
  LANG: "C.UTF-8",
  TERM: "xterm-256color",
  USER: "guest",
  PATH: "/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin",
};

// Commands offered by tab-completion (matches the switch in run()).
const COMMANDS = [
  "cat", "cd", "clear", "curl", "date", "df", "echo", "env", "exit", "free",
  "grep", "head", "help", "history", "hostname", "id", "ls", "mkdir", "ping",
  "printenv", "ps", "pwd", "rm", "ssh", "su", "sudo", "tail", "touch", "uname",
  "uptime", "wget", "which", "whoami",
];

const CHAR_MS = 22;

// ---------------------------------------------------------------------------
// path helpers
// ---------------------------------------------------------------------------

function resolvePath(p: string, cwd: string): string {
  if (!p || p === "~") p = p === "~" ? HOME : cwd;
  else if (p.startsWith("~/")) p = HOME + p.slice(1);
  const abs = p.startsWith("/") ? p : cwd + "/" + p;
  const out: string[] = [];
  for (const seg of abs.split("/")) {
    if (!seg || seg === ".") continue;
    if (seg === "..") out.pop();
    else out.push(seg);
  }
  return "/" + out.join("/");
}

function childrenOf(dir: string): string[] {
  const prefix = dir === "/" ? "/" : dir + "/";
  return Object.keys(FS)
    .filter(
      (k) =>
        k !== dir && k.startsWith(prefix) && !k.slice(prefix.length).includes("/")
    )
    .map((k) => k.slice(prefix.length));
}

function tildify(cwd: string): string {
  if (cwd === HOME) return "~";
  if (cwd.startsWith(HOME + "/")) return "~" + cwd.slice(HOME.length);
  return cwd;
}

const nodeSize = (n: FsNode) =>
  n.dir ? 4096 : n.size ?? (n.content ? n.content.join("\n").length + 1 : 0);

// `ls` sorts ignoring leading dots (like LC_COLLATE=en_US does).
const lsSort = (names: string[]) =>
  [...names].sort((a, b) =>
    a.replace(/^\.+/, "").toLowerCase().localeCompare(b.replace(/^\.+/, "").toLowerCase())
  );

function longLines(entries: { name: string; node: FsNode }[], withTotal: boolean): string[] {
  const sizeW = Math.max(...entries.map((e) => String(nodeSize(e.node)).length));
  const ownerW = Math.max(...entries.map((e) => e.node.owner.length));
  const groupW = Math.max(...entries.map((e) => e.node.group.length));
  const lines = entries.map(({ name, node }) => {
    const links = node.dir ? 2 : 1;
    return `${node.mode} ${links} ${node.owner.padEnd(ownerW)} ${node.group.padEnd(groupW)} ${String(nodeSize(node)).padStart(sizeW)} ${node.date} ${name}`;
  });
  if (!withTotal) return lines;
  const total = entries.reduce(
    (a, e) => a + (e.node.dir ? 4 : Math.max(4, Math.ceil(nodeSize(e.node) / 1024) * 4)),
    0
  );
  return [`total ${total}`, ...lines];
}

// ---------------------------------------------------------------------------
// fixed "system state" output
// ---------------------------------------------------------------------------

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n: number) => String(n).padStart(2, "0");

// The box thinks it's in UTC, like any self-respecting server.
function unixDate(now: Date): string {
  return `${DAYS[now.getUTCDay()]} ${MONTHS[now.getUTCMonth()]} ${String(now.getUTCDate()).padStart(2, " ")} ${pad2(now.getUTCHours())}:${pad2(now.getUTCMinutes())}:${pad2(now.getUTCSeconds())} UTC ${now.getUTCFullYear()}`;
}

function uptimeLine(now: Date): string {
  return ` ${pad2(now.getUTCHours())}:${pad2(now.getUTCMinutes())}:${pad2(now.getUTCSeconds())} up 42 days,  3:17,  1 user,  load average: 0.00, 0.02, 0.05`;
}

const PS_SHORT = [
  "    PID TTY          TIME CMD",
  "   4821 pts/0    00:00:00 rbash",
  "   4907 pts/0    00:00:00 ps",
];

const PS_LONG = [
  "USER         PID %CPU %MEM    VSZ   RSS TTY      STAT START   TIME COMMAND",
  "root           1  0.0  0.2 167744 11908 ?        Ss   May25   0:14 /sbin/init",
  "root         412  0.0  0.1  15436  6812 ?        Ss   May25   0:00 sshd: /usr/sbin/sshd -D [listener] 0 of 10-100 startups",
  "root         617  0.0  0.0  58720  1552 ?        Ss   May25   0:00 nginx: master process /usr/sbin/nginx",
  "www-data     618  0.0  0.3  59204 14200 ?        S    May25   1:02 nginx: worker process",
  "guest       4821  0.0  0.1   8476  5324 pts/0    Ss   09:41   0:00 -rbash",
  "guest       4907  0.0  0.0  10460  3888 pts/0    R+   09:41   0:00 ps aux",
];

const DF_H = [
  "Filesystem      Size  Used Avail Use% Mounted on",
  "/dev/vda1        39G   12G   26G  32% /",
  "tmpfs           1.9G     0  1.9G   0% /dev/shm",
  "tmpfs           392M  1.1M  391M   1% /run",
];

const DF_PLAIN = [
  "Filesystem     1K-blocks     Used Available Use% Mounted on",
  "/dev/vda1       40894464 12123648  26658816  32% /",
  "tmpfs            2010920        0   2010920   0% /dev/shm",
  "tmpfs             401084     1128    399956   1% /run",
];

const FREE_H = [
  "               total        used        free      shared  buff/cache   available",
  "Mem:           3.8Gi       412Mi       2.9Gi       8.0Mi       548Mi       3.2Gi",
  "Swap:             0B          0B          0B",
];

const FREE_PLAIN = [
  "               total        used        free      shared  buff/cache   available",
  "Mem:         4021840      422136     3037884        8204      561820     3341368",
  "Swap:              0           0           0",
];

// ---------------------------------------------------------------------------
// tab completion
// ---------------------------------------------------------------------------

// Longest string that's a prefix of every candidate.
function commonPrefix(items: string[]): string {
  if (items.length === 0) return "";
  let prefix = items[0];
  for (const s of items.slice(1)) {
    while (!s.startsWith(prefix)) prefix = prefix.slice(0, -1);
  }
  return prefix;
}

// Tab-completion: first word completes a command name, everything after
// completes a path (like default bash filename completion). Hidden dotfiles
// only surface once the token itself starts with a dot — like a real shell,
// so a blank tab won't spoil the easter egg.
function complete(value: string, cwd: string): { next: string; show?: string[] } {
  const trailingSpace = /\s$/.test(value);
  const parts = value.split(/\s+/).filter(Boolean);

  if (parts.length <= 1 && !trailingSpace) {
    const token = (parts[0] ?? "").toLowerCase();
    const matches = COMMANDS.filter((c) => c.startsWith(token));
    if (matches.length === 0) return { next: value };
    if (matches.length === 1) return { next: matches[0] + " " };
    const cp = commonPrefix(matches);
    return { next: cp.length > token.length ? cp : value, show: matches };
  }

  const token = trailingSpace ? "" : parts[parts.length - 1];
  const slash = token.lastIndexOf("/");
  const dirPart = slash >= 0 ? token.slice(0, slash + 1) : "";
  const base = slash >= 0 ? token.slice(slash + 1) : token;
  const dirAbs = resolvePath(dirPart, cwd);
  const dirNode = FS[dirAbs];
  if (!dirNode?.dir || dirNode.denied) return { next: value };

  let names = childrenOf(dirAbs);
  if (!base.startsWith(".")) names = names.filter((n) => !n.startsWith("."));
  const matches = lsSort(names.filter((n) => n.startsWith(base)));
  if (matches.length === 0) return { next: value };

  const replaceToken = (completed: string) =>
    trailingSpace ? value + completed : value.slice(0, value.length - token.length) + completed;

  if (matches.length === 1) {
    const isDir = !!FS[resolvePath(dirPart + matches[0], cwd)]?.dir;
    return { next: replaceToken(dirPart + matches[0] + (isDir ? "/" : " ")) };
  }
  const cp = commonPrefix(matches);
  return {
    next: cp.length > base.length ? replaceToken(dirPart + cp) : value,
    show: matches,
  };
}

// ---------------------------------------------------------------------------
// the shell
// ---------------------------------------------------------------------------

type RunResult = {
  lines: string[];
  clear?: boolean;
  redirect?: string;
  href?: string; // full page load — for paths outside this Next app
  unknown?: boolean; // command not found — reported to the backend
  cwd?: string;
  exit?: boolean;
};

function run(raw: string, cwd: string, past: string[]): RunResult {
  const c = raw.trim();
  if (!c) return { lines: [] };

  // The code from the hidden file — paste it to unlock the unlisted page.
  if (c === UNLOCK_CODE) {
    return {
      lines: ["access granted ✦ opening the backdoor…"],
      redirect: SECRET_PATH,
    };
  }

  const parts = c.split(/\s+/);
  const name = parts[0] ?? "";
  const rest = parts.slice(1);
  const flags = rest.filter((a) => a.startsWith("-"));
  const args = rest.filter((a) => !a.startsWith("-"));
  const hasFlag = (letter: string) =>
    flags.some((fl) => !fl.startsWith("--") && fl.includes(letter));

  switch (name.toLowerCase()) {
    case "help":
      return {
        lines: [
          "rbash: restricted shell. available commands:",
          COMMANDS.join("  "),
        ],
      };

    case "pwd":
      return { lines: [cwd] };

    case "cd": {
      const target = resolvePath(args[0] ?? "~", cwd);
      const node = FS[target];
      if (!node) return { lines: [`-rbash: cd: ${args[0]}: No such file or directory`] };
      if (!node.dir) return { lines: [`-rbash: cd: ${args[0]}: Not a directory`] };
      if (node.denied) return { lines: [`-rbash: cd: ${args[0]}: Permission denied`] };
      return { lines: [], cwd: target };
    }

    case "ls": {
      const target = resolvePath(args[0] ?? ".", cwd);
      const node = FS[target];
      if (!node) {
        return { lines: [`ls: cannot access '${args[0]}': No such file or directory`] };
      }
      if (!node.dir) {
        return {
          lines: hasFlag("l")
            ? longLines([{ name: args[0], node }], false)
            : [args[0]],
        };
      }
      if (node.denied) {
        return { lines: [`ls: cannot open directory '${args[0] ?? target}': Permission denied`] };
      }
      let names = lsSort(childrenOf(target));
      if (!hasFlag("a")) names = names.filter((n) => !n.startsWith("."));
      if (hasFlag("l")) {
        const entries = [
          ...(hasFlag("a")
            ? [
                { name: ".", node },
                { name: "..", node: FS[resolvePath("..", target)] ?? node },
              ]
            : []),
          ...names.map((n) => ({
            name: n,
            node: FS[(target === "/" ? "" : target) + "/" + n],
          })),
        ];
        return { lines: longLines(entries, true) };
      }
      if (hasFlag("a")) names = [".", "..", ...names];
      return { lines: names.length ? [names.join("  ")] : [] };
    }

    case "cat":
    case "head":
    case "tail": {
      if (args.length === 0) return { lines: [] };
      const lines: string[] = [];
      // head/tail honour `-n N` (or `-5`); default 10.
      let count = 10;
      const nIdx = rest.indexOf("-n");
      if (nIdx >= 0 && rest[nIdx + 1]) count = parseInt(rest[nIdx + 1], 10) || 10;
      const shortFlag = flags.find((fl) => /^-\d+$/.test(fl));
      if (shortFlag) count = parseInt(shortFlag.slice(1), 10);
      for (const a of args) {
        if (nIdx >= 0 && a === rest[nIdx + 1]) continue;
        const target = resolvePath(a, cwd);
        const node = FS[target];
        if (!node) lines.push(`${name}: ${a}: No such file or directory`);
        else if (node.dir) lines.push(`${name}: ${a}: Is a directory`);
        else if (node.denied) lines.push(`${name}: ${a}: Permission denied`);
        else {
          const content = node.content ?? [];
          if (name === "head") lines.push(...content.slice(0, count));
          else if (name === "tail") lines.push(...content.slice(-count));
          else lines.push(...content);
        }
      }
      return { lines };
    }

    case "grep": {
      if (rest.length < 2) return { lines: ["usage: grep [OPTION]... PATTERNS [FILE]..."] };
      const [pattern, ...files] = args.length >= 2 ? args : [rest[0], ...rest.slice(1)];
      let re: RegExp | null = null;
      try {
        re = new RegExp(pattern);
      } catch {
        /* fall back to substring match */
      }
      const lines: string[] = [];
      for (const file of files) {
        const target = resolvePath(file, cwd);
        const node = FS[target];
        if (!node) lines.push(`grep: ${file}: No such file or directory`);
        else if (node.dir) lines.push(`grep: ${file}: Is a directory`);
        else if (node.denied) lines.push(`grep: ${file}: Permission denied`);
        else {
          const hits = (node.content ?? []).filter((l) =>
            re ? re.test(l) : l.includes(pattern)
          );
          lines.push(...(files.length > 1 ? hits.map((h) => `${file}:${h}`) : hits));
        }
      }
      return { lines };
    }

    case "echo": {
      let text = c.slice(name.length).trim();
      if (
        (text.startsWith('"') && text.endsWith('"')) ||
        (text.startsWith("'") && text.endsWith("'"))
      ) {
        text = text.slice(1, -1);
      }
      text = text.replace(/\$(\w+)/g, (_, k: string) =>
        k === "PWD" ? cwd : ENV[k] ?? ""
      );
      return { lines: [text] };
    }

    case "env":
    case "printenv":
      return {
        lines: [...Object.entries(ENV).map(([k, v]) => `${k}=${v}`), `PWD=${cwd}`],
      };

    case "whoami":
      return { lines: ["guest"] };

    case "id":
      return { lines: ["uid=1001(guest) gid=1001(guest) groups=1001(guest)"] };

    case "hostname":
      return { lines: [HOST] };

    case "uname": {
      if (hasFlag("a")) {
        return {
          lines: [
            "Linux backend 6.1.0-18-amd64 #1 SMP PREEMPT_DYNAMIC Debian 6.1.76-1 (2024-02-01) x86_64 GNU/Linux",
          ],
        };
      }
      if (hasFlag("r")) return { lines: ["6.1.0-18-amd64"] };
      if (hasFlag("m")) return { lines: ["x86_64"] };
      return { lines: ["Linux"] };
    }

    case "date":
      return { lines: [unixDate(new Date())] };

    case "uptime":
      return { lines: [uptimeLine(new Date())] };

    case "ps":
      return { lines: rest.length > 0 ? PS_LONG : PS_SHORT };

    case "df":
      return { lines: hasFlag("h") ? DF_H : DF_PLAIN };

    case "free":
      return { lines: hasFlag("h") ? FREE_H : FREE_PLAIN };

    case "history":
      return {
        lines: past.map((cmd, i) => `${String(i + 1).padStart(5)}  ${cmd}`),
      };

    case "which": {
      const lines: string[] = [];
      for (const a of args) {
        if (FS["/bin/" + a]) lines.push("/bin/" + a);
        else if (FS["/usr/bin/" + a]) lines.push("/usr/bin/" + a);
      }
      return { lines };
    }

    case "sudo":
      if (args[0]?.toLowerCase() === "ascend") {
        return {
          lines: [
            "[AUTH] requesting cosmic privileges...",
            "[OK] root@universe acquired (simulated) ✦ elevating...",
          ],
          href: ASCEND_PATH,
        };
      }
      return {
        lines: ["guest is not in the sudoers file.  This incident will be reported."],
      };

    case "su":
      return { lines: ["su: Authentication failure"] };

    case "ssh": {
      if (args.length === 0) {
        return {
          lines: [
            "usage: ssh [-46AaCfGgKkMNnqsTtVvXxYy] [-B bind_interface] [-b bind_address]",
            "           [-c cipher_spec] [-D [bind_address:]port] [-E log_file]",
            "           [-e escape_char] [-F configfile] [-i identity_file] destination [command]",
          ],
        };
      }
      const host = args[0].includes("@") ? args[0].split("@")[1] : args[0];
      return { lines: [`ssh: connect to host ${host} port 22: Connection refused`] };
    }

    case "ping": {
      if (args.length === 0) return { lines: ["ping: usage error: Destination address required"] };
      return { lines: ["ping: connect: Network is unreachable"] };
    }

    case "curl": {
      if (rest.length === 0) {
        return { lines: ["curl: try 'curl --help' or 'curl --manual' for more information"] };
      }
      const host = (args[0] ?? "").replace(/^https?:\/\//, "").split("/")[0] || args[0];
      return { lines: [`curl: (6) Could not resolve host: ${host}`] };
    }

    case "wget": {
      if (rest.length === 0) return { lines: ["wget: missing URL"] };
      const host = (args[0] ?? "").replace(/^https?:\/\//, "").split("/")[0] || args[0];
      return {
        lines: [
          `--${unixDate(new Date()).slice(4)}--  ${args[0]}`,
          `Resolving ${host} (${host})... failed: Temporary failure in name resolution.`,
          `wget: unable to resolve host address '${host}'`,
        ],
      };
    }

    case "rm": {
      if (args.length === 0) return { lines: ["rm: missing operand", "Try 'rm --help' for more information."] };
      return {
        lines: args.map((a) =>
          FS[resolvePath(a, cwd)]
            ? `rm: cannot remove '${a}': Read-only file system`
            : `rm: cannot remove '${a}': No such file or directory`
        ),
      };
    }

    case "touch":
      if (args.length === 0) return { lines: ["touch: missing file operand"] };
      return {
        lines: args.map((a) => `touch: cannot touch '${a}': Read-only file system`),
      };

    case "mkdir":
      if (args.length === 0) return { lines: ["mkdir: missing operand"] };
      return {
        lines: args.map((a) => `mkdir: cannot create directory '${a}': Read-only file system`),
      };

    case "clear":
      return { lines: [], clear: true };

    case "exit":
    case "logout":
      return { lines: ["logout"], exit: true };

    default: {
      // `./flag.txt` style execution attempts get real errors too.
      if (name.includes("/")) {
        const node = FS[resolvePath(name, cwd)];
        return {
          lines: [
            node
              ? `-rbash: ${name}: Permission denied`
              : `-rbash: ${name}: No such file or directory`,
          ],
        };
      }
      return { lines: [`-rbash: ${name}: command not found`], unknown: true };
    }
  }
}

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------

export default function BackendConsole({
  active,
  onExit,
}: {
  active: boolean;
  onExit?: () => void;
}) {
  const [bootShown, setBootShown] = useState(0);
  const [booted, setBooted] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [cwd, setCwd] = useState(HOME);
  // Submitted commands, newest last, for up/down recall. `cursor` is how many
  // steps back we are (0 = live input, not in history yet).
  const [commands, setCommands] = useState<string[]>([]);
  const [cursor, setCursor] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();

  const prompt = `guest@${HOST}:${tildify(cwd)}$`;

  // Boot each time the backend opens; full reset when it closes.
  useEffect(() => {
    if (!active) {
      // Reset all console state when it closes, so the next open boots fresh.
      /* eslint-disable react-hooks/set-state-in-effect */
      setBootShown(0);
      setBooted(false);
      setHistory([]);
      setInput("");
      setCwd(HOME);
      setCommands([]);
      setCursor(0);
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    navigator.vibrate?.(12);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setBootShown(i);
      if (i >= BOOT.length) {
        clearInterval(id);
        setBooted(true);
        // Auto-focus only with a real keyboard — don't pop the on-screen
        // keyboard unprompted on touch devices.
        if (window.matchMedia("(pointer: fine)").matches) {
          inputRef.current?.focus();
        }
      }
    }, CHAR_MS);

    return () => clearInterval(id);
  }, [active]);

  // Keep the latest output in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history, bootShown, booted]);

  if (!active) return null;

  const submit = () => {
    const value = input;
    setInput("");
    setCursor(0);
    // Record non-empty commands for up/down recall (skip exact repeats).
    const nextCommands =
      value.trim() && commands[commands.length - 1] !== value
        ? [...commands, value]
        : commands;
    setCommands(nextCommands);
    // A quiet "we saw that" — only visible to anyone who opens devtools.
    console.log("%c[backend] logged: " + (value || "(empty)"), "color:#888");
    const result = run(value, cwd, nextCommands);
    if (result.clear) {
      setHistory([]);
      return;
    }
    setHistory((h) => [...h, `${prompt} ${value}`, ...result.lines]);
    if (result.unknown) {
      // The backend really does read what you type. Anything it has to say
      // back gets appended under the error.
      void fetch("/api/console", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cmd: value }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { lines?: unknown } | null) => {
          const lines = Array.isArray(data?.lines)
            ? data.lines.filter((l): l is string => typeof l === "string")
            : [];
          if (lines.length) setHistory((h) => [...h, ...lines]);
        })
        .catch(() => {});
    }
    if (result.cwd) setCwd(result.cwd);
    if (result.exit) {
      // `logout` prints, then the session closes — like dropping the ssh
      // connection.
      setTimeout(() => onExit?.(), 350);
      return;
    }
    if (result.href) {
      // /ascend/ is proxied to another container, so the Next router can't
      // reach it — do a real navigation after the same dramatic pause.
      const href = result.href;
      setTimeout(() => window.location.assign(href), 900);
      return;
    }
    if (result.redirect) {
      // Solving the puzzle grants a short-lived invite to escalate privileges
      // (mint a member account) on the page we're about to open. Wait for the
      // invite cookie to actually be set before navigating: a fire-and-forget
      // fetch raced a fixed timer, so on a slow (prod) round-trip /backdoor
      // could load without the cookie and "Escalate" would 403.
      const redirect = result.redirect;
      void (async () => {
        const invited = fetch("/api/members/invite", { method: "POST" }).catch(
          () => {}
        );
        // Keep the brief dramatic pause, but never navigate before the cookie
        // lands — whichever takes longer wins.
        await Promise.all([invited, new Promise((r) => setTimeout(r, 900))]);
        router.push(redirect);
      })();
    }
  };

  const bootLines = BOOT.slice(0, bootShown).split("\n");

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center px-4 pt-20 sm:pt-24">
      <div
        ref={scrollRef}
        onClick={() => inputRef.current?.focus()}
        className="pointer-events-auto h-[min(55dvh,24rem)] w-[min(92vw,40rem)] overflow-y-auto rounded-md border border-primary/40 bg-black/75 p-4 font-mono text-[13px] leading-relaxed text-primary shadow-[0_0_60px_-15px_var(--primary)] backdrop-blur-[2px]"
      >
        {bootLines.map((line, i) => (
          <div key={`b-${i}`} className="whitespace-pre-wrap">
            {line || " "}
          </div>
        ))}

        {history.map((line, i) => (
          <div key={`h-${i}`} className="whitespace-pre-wrap">
            {line || " "}
          </div>
        ))}

        {booted && (
          <div className="flex items-center whitespace-pre-wrap">
            <span className="shrink-0">{prompt}&nbsp;</span>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && (e.key === "c" || e.key === "C")) {
                  // ^C abandons the current line — unless the user is copying
                  // selected text, which should still work.
                  if (window.getSelection()?.toString()) return;
                  e.preventDefault();
                  setHistory((h) => [...h, `${prompt} ${input}^C`]);
                  setInput("");
                  setCursor(0);
                } else if (e.ctrlKey && (e.key === "l" || e.key === "L")) {
                  e.preventDefault();
                  setHistory([]);
                } else if (e.key === "Enter") {
                  submit();
                } else if (e.key === "ArrowUp") {
                  // Walk back through past commands.
                  e.preventDefault();
                  if (commands.length === 0) return;
                  const next = Math.min(cursor + 1, commands.length);
                  setCursor(next);
                  setInput(commands[commands.length - next]);
                } else if (e.key === "ArrowDown") {
                  // Walk forward, back to a blank live prompt.
                  e.preventDefault();
                  if (cursor === 0) return;
                  const next = cursor - 1;
                  setCursor(next);
                  setInput(next === 0 ? "" : commands[commands.length - next]);
                } else if (e.key === "Tab") {
                  e.preventDefault();
                  const { next, show } = complete(input, cwd);
                  setInput(next);
                  if (show) {
                    setHistory((h) => [
                      ...h,
                      `${prompt} ${input}`,
                      show.join("  "),
                    ]);
                  }
                }
              }}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              aria-label="backend console input"
              className="flex-1 border-0 bg-transparent p-0 text-primary caret-primary outline-none focus:ring-0"
            />
          </div>
        )}
      </div>
    </div>
  );
}
